import { uiScaleOf, type UiView } from "odyssey-shared";

/**
 * The UI scale the canvas follows (ADR-061 decision 5 as amended): the same whole number the overlay root
 * carries as `--ui-scale`, recomputed whenever the window's device size changes, so the UI camera and the
 * DOM always draw one logical pixel as the same number of device pixels.
 */

/**
 * A UI scale that a camera or a surface follows.
 */
export interface UiScaleSource {
  /** The scale now. */
  readonly current: number;
  /**
   * Calls back with each new scale.
   *
   * @param listener Called with the scale whenever it changes.
   * @returns A function that stops the calls.
   */
  subscribe(listener: (scale: number) => void): () => void;
}

/**
 * The page's UI scale, computed from the window's device size.
 */
export class UiScale implements UiScaleSource {
  private scale: number;
  private readonly listeners = new Set<(scale: number) => void>();

  /**
   * @param device The canvas's size in device pixels.
   */
  constructor(device: UiView) {
    this.scale = uiScaleOf(device);
  }

  /**
   * The scale now.
   *
   * @returns The scale.
   */
  get current(): number {
    return this.scale;
  }

  /**
   * Recomputes the scale for a new device size and tells the listeners when it moved.
   *
   * @param device The canvas's size in device pixels.
   */
  resize(device: UiView): void {
    const scale = uiScaleOf(device);
    if (scale === this.scale) {
      return;
    }
    this.scale = scale;
    for (const listener of this.listeners) {
      listener(scale);
    }
  }

  /**
   * Calls back with each new scale.
   *
   * @param listener Called with the scale whenever it changes.
   * @returns A function that stops the calls.
   */
  subscribe(listener: (scale: number) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
